/* eslint-disable import/no-named-as-default */
import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { Routes as AppRoutes } from './config/routes';
import { ValidationProvider } from './providers/ValidationContext';
import { EmailValidationProvider } from './providers/EmailValidationContext';
import { LinkDevicesProvider } from './providers/LinkDevicesContext';
import { ScopeRequestProvider } from './providers/ScopeRequestContext';
import AuthenticationScreen from './screens/authentication/AuthenticationScreen';
import CancelScreen from './screens/CancelScreen';
import CaptchaScreen from './screens/CaptchaScreen';
import ConfirmWalletTransactionScreen from './screens/ConfirmWalletTransactionScreen';
import DeclarationsScreen from './screens/DeclarationsScreen';
import ContinueScreen from './screens/validation/ContinueScreen';
import DocumentCaptureScreen from './screens/document/DocumentCapture';
import ValidationErrorScreen from './screens/validation/ValidationError';
import ValidationFinalizingScreen from './screens/validation/ValidationFinalizing';
import ValidationInProgressScreen from './screens/validation/ValidationInProgress';
import ValidationInReviewScreen from './screens/validation/ValidationInReview';
import ValidationNextStepScreen from './screens/validation/ValidationNextStep';
import ValidationRejectedScreen from './screens/validation/ValidationRejected';
import DocumentSelfieScreen from './screens/faceScan/DocumentSelfie';
import ValidationStatusScreen from './screens/validation/ValidationStatus';
import EmailCapture from './screens/email/EmailScreen';
import EmailVerify from './screens/email/EmailVerifyScreen';
import DateOfBirthScreen from './screens/dateOfBirth/DateOfBirthScreen';
import ChainErrorScreen from './screens/errors/ChainErrorScreen';
import ErrorScreen from './screens/errors/ErrorScreen';
import SupportScreen from './screens/errors/SupportScreen';
import CaptchaExplainerScreen from './screens/ExplainerScreen/CaptchaExplainerScreen';
import TestExplainerScreen from './screens/ExplainerScreen/TestExplainerScreen';
import DocumentExplainerScreen from './screens/ExplainerScreen/DocumentExplainerScreen';
import AgeCheck13ExplainerScreen from './screens/ExplainerScreen/AgeCheck13ExplainerScreen';
import FaceAgeCheck13ExplainerScreen from './screens/ExplainerScreen/FaceAgeCheck13ExplainerScreen';
import SipExplainerScreen from './screens/ExplainerScreen/SipExplainerScreen';
import UniquenessExplainerScreen from './screens/ExplainerScreen/UniquenessExplainerScreen';
import LivenessExplainerScreen from './screens/ExplainerScreen/LivenessExplainerScreen';
import FaceAuthenticateScreen from './screens/faceScan/FaceAuthenticate';
import FaceUniquenessScreen from './screens/faceScan/FaceUniqueness';
import FaceSearchScreen from './screens/faceScan/FaceSearchScreen';
import RevocationQrCodeScreen from './screens/revocation/RevocationQrCodeScreen';
import FailedIPCheckScreen from './screens/FailedIPCheckScreen';
import FailedVPNCheckScreen from './screens/FailedVPNCheckScreen';
import GenericStatusScreen from './screens/GenericStatusScreen';
import ContinueWithLinkScreen from './screens/validation/linkDevices/ContinueWithLinkScreen';
import DeviceLinkedScreen from './screens/validation/linkDevices/DeviceLinkedScreen';
import LinkDevicesScreen from './screens/validation/linkDevices/LinkDevicesScreen';
import UploadCompleteScreen from './screens/validation/linkDevices/UploadCompleteScreen';
import NoFlowScreen from './screens/NoFlow';
import QrCodeScreen from './screens/QrCodeScreen';
import AwaitingTransactionConfirmation from './screens/TokenStatus/AwaitingTransactionConfirmation';
import SignTransactionScreen from './screens/TokenStatus/SignTransactionScreen';
import TokenActiveScreen from './screens/TokenStatus/TokenActiveScreen';
import TokenFrozenScreen from './screens/TokenStatus/TokenFrozenScreen';
import TokenInReviewScreen from './screens/TokenStatus/TokenInReviewScreen';
import TokenRefreshInReviewScreen from './screens/TokenStatus/TokenRefreshInReviewScreen';
import TokenInPartnerReviewScreen from './screens/TokenStatus/TokenInPartnerReviewScreen';
import TokenManualReviewScreen from './screens/TokenStatus/TokenManualReviewScreen';
import TokenRejectedScreen from './screens/TokenStatus/TokenRejectedScreen';
import TokenRevokedScreen from './screens/TokenStatus/TokenRevokedScreen';
import WalletOwnershipScreen from './screens/WalletOwnershipScreen';
import WalletTokenSuccessScreen from './screens/WalletTokenSuccessScreen';
import ThemeSwitchDevPage from './common/components/dev/ThemeSwitchDevPage';
import AuthenticationOnlyScreen from './screens/AuthenticationOnlyScreen';
import {
  UnsupportedVersion,
  CivicSignsNotSupported,
  InvalidConfiguration,
} from './screens/errors/InvalidConfigErrorScreens';
import SharePersonalDetailsScreen from './screens/SharePersonalDetailsScreen';
import { CredentialShareProvider } from './providers/CredentialShareContext';
import ShareCaptureDetailsScreen from './screens/ShareCaptureDetailsScreen';
import RevocationDisclaimerScreen from './screens/revocation/RevocationDisclaimerScreen';
import RevocationPassesListScreen from './screens/revocation/RevocationPassesListScreen';
import RevocationInReviewScreen from './screens/revocation/RevocationInReviewScreen';
import { RevocationProvider } from './providers/RevocationContext';
import RevocationConfirmationScreen from './screens/revocation/RevocationConfirmationScreen';
import CivicDemoScreen from './screens/civicDemo/CivicDemoScreen';
import ExplainerScreen from './screens/ExplainerScreen/ExplainerScreen';
import { CredentialStoreProvider } from './providers/CredentialStoreContext';
import StoreCredentialsScreen from './screens/StoreCredentialsScreen';
import CheckForExistingCredentialsScreen from './screens/CheckForExistingCredentialsScreen';
import ChooseCredentialsScreen from './screens/ChooseCredentialsScreen';
import CredentialExpiredScreen from './screens/CredentialExpiredScreen';
import FundsCheckScreen from './screens/TokenStatus/FundsCheckScreen';

export default function App(): JSX.Element {
  return (
    <>
      <Routes>
        <Route path={AppRoutes.SIGN_TRANSACTION} element={<SignTransactionScreen />} />
        <Route path={AppRoutes.TOKEN_REFRESH_IN_REVIEW} element={<TokenRefreshInReviewScreen />} />
      </Routes>
      <ScopeRequestProvider>
        <>
          <CredentialStoreProvider>
            <>
              <Routes>
                <Route path={AppRoutes.QR_CODE} element={<QrCodeScreen />} />
                <Route path={AppRoutes.CANCEL} element={<CancelScreen />} />
                <Route path={AppRoutes.ERROR} element={<ErrorScreen />} />
                <Route path={AppRoutes.CHAIN_ERROR} element={<ChainErrorScreen />} />
              </Routes>
              <ValidationProvider>
                <>
                  <Routes>
                    <Route
                      path={AppRoutes.CHECK_FOR_EXISTING_CREDENTIALS}
                      element={<CheckForExistingCredentialsScreen />}
                    />
                    <Route path={AppRoutes.CHOOSE_CREDENTIALS} element={<ChooseCredentialsScreen />} />
                    <Route path={AppRoutes.FUNDS_CHECK} element={<FundsCheckScreen />} />
                    <Route path={AppRoutes.TOKEN_ACTIVE} element={<TokenActiveScreen />} />
                    <Route path={AppRoutes.EXPLAINER_DOCUMENT} element={<DocumentExplainerScreen />} />
                    <Route path={AppRoutes.EXPLAINER_UNIQUENESS} element={<UniquenessExplainerScreen />} />
                    <Route path={AppRoutes.EXPLAINER_LIVENESS} element={<LivenessExplainerScreen />} />
                    <Route path={AppRoutes.EXPLAINER_FACE_AGE_CHECK_13} element={<FaceAgeCheck13ExplainerScreen />} />
                    <Route path={AppRoutes.LOGIN} element={<AuthenticationScreen />} />
                    <Route
                      path={AppRoutes.START_PRE_APPROVED_TRANSACTION}
                      element={<SignTransactionScreen preApprovedTxMode />}
                    />
                    <Route path={AppRoutes.DECLARATIONS} element={<DeclarationsScreen />} />
                    <Route path={AppRoutes.VALIDATION_NEXT_STEP} element={<ValidationNextStepScreen />} />
                    <Route path={AppRoutes.DOCUMENT_SELFIE} element={<DocumentSelfieScreen />} />
                    <Route path={AppRoutes.FACE_AUTH} element={<FaceAuthenticateScreen />} />
                    <Route path={AppRoutes.DOCUMENT_CAPTURE} element={<DocumentCaptureScreen />} />
                    <Route path={AppRoutes.CONTINUE} element={<ContinueScreen />} />
                    <Route path={AppRoutes.CREDENTIAL_EXPIRED} element={<CredentialExpiredScreen />} />
                    <Route path={AppRoutes.VALIDATION_IN_PROGRESS} element={<ValidationInProgressScreen />} />
                    <Route path={AppRoutes.VALIDATION_IN_REVIEW} element={<ValidationInReviewScreen />} />
                    <Route path={AppRoutes.VALIDATION_ERROR} element={<ValidationErrorScreen />} />
                    <Route path={AppRoutes.VALIDATION_REJECTED} element={<ValidationRejectedScreen />} />
                    <Route path={AppRoutes.VALIDATION_STATUS} element={<ValidationStatusScreen />} />
                    <Route path={AppRoutes.SUPPORT} element={<SupportScreen />} />
                    <Route path={AppRoutes.FACE_UNIQUENESS} element={<FaceUniquenessScreen />} />
                  </Routes>
                  <EmailValidationProvider>
                    <Routes>
                      <Route path={AppRoutes.EMAIL_CAPTURE} element={<EmailCapture />} />
                      <Route path={AppRoutes.EMAIL_VERIFY} element={<EmailVerify />} />
                    </Routes>
                  </EmailValidationProvider>
                  <LinkDevicesProvider>
                    <Routes>
                      <Route path={AppRoutes.CONTINUE_WITH_LINK} element={<ContinueWithLinkScreen />} />
                      <Route path={AppRoutes.DEVICE_LINKED} element={<DeviceLinkedScreen />} />
                      <Route path={AppRoutes.LINK_DEVICES} element={<LinkDevicesScreen />} />
                      <Route path={AppRoutes.UPLOAD_COMPLETE} element={<UploadCompleteScreen />} />
                    </Routes>
                  </LinkDevicesProvider>

                  <CredentialShareProvider>
                    <Routes>
                      <Route path={AppRoutes.STORE_CREDENTIALS} element={<StoreCredentialsScreen />} />
                      <Route path={AppRoutes.SHARE_PERSONAL_DETAILS} element={<SharePersonalDetailsScreen />} />
                      <Route path={AppRoutes.CAPTURE_PERSONAL_DETAILS} element={<ShareCaptureDetailsScreen />} />
                      <Route path={AppRoutes.VALIDATION_FINALIZING} element={<ValidationFinalizingScreen />} />
                    </Routes>
                  </CredentialShareProvider>
                </>
              </ValidationProvider>
            </>
          </CredentialStoreProvider>
        </>
      </ScopeRequestProvider>

      <Routes>
        <Route path={AppRoutes.CIVIC_DEMO} element={<CivicDemoScreen />} />
        <Route path={AppRoutes.DOB_CAPTURE} element={<DateOfBirthScreen />} />
        <Route path={AppRoutes.CAPTCHA} element={<CaptchaScreen />} />
        <Route path={AppRoutes.CONFIRM_WALLET_TRANSACTION} element={<ConfirmWalletTransactionScreen />} />
        <Route path={AppRoutes.TOKEN_FROZEN} element={<TokenFrozenScreen />} />
        <Route path={AppRoutes.TOKEN_REVOKED} element={<TokenRevokedScreen />} />
        <Route path={AppRoutes.TOKEN_REJECTED} element={<TokenRejectedScreen />} />
        <Route path={AppRoutes.FAILED_IP_CHECK} element={<FailedIPCheckScreen />} />
        <Route path={AppRoutes.FAILED_VPN_CHECK} element={<FailedVPNCheckScreen />} />
        <Route path={AppRoutes.TOKEN_IN_REVIEW} element={<TokenInReviewScreen />} />
        <Route path={AppRoutes.TOKEN_IN_PARTNER_REVIEW} element={<TokenInPartnerReviewScreen />} />
        <Route path={AppRoutes.TOKEN_MANUAL_REVIEW} element={<TokenManualReviewScreen />} />
        <Route path={AppRoutes.PROOF_OF_WALLET_OWNERSHIP} element={<WalletOwnershipScreen />} />
        <Route path={AppRoutes.AUTHENTICATION_ONLY} element={<AuthenticationOnlyScreen />} />
        <Route path={AppRoutes.NO_FLOW} element={<NoFlowScreen />} />
        <Route path={AppRoutes.EXPLAINER} element={<ExplainerScreen />} />
        <Route path={AppRoutes.EXPLAINER_SIP} element={<SipExplainerScreen />} />
        <Route path={AppRoutes.EXPLAINER_TEST} element={<TestExplainerScreen />} />
        <Route path={AppRoutes.EXPLAINER_CAPTCHA} element={<CaptchaExplainerScreen />} />
        <Route path={AppRoutes.EXPLAINER_AGE_CHECK_13} element={<AgeCheck13ExplainerScreen />} />
        <Route path={AppRoutes.GENERIC_STATUS_SCREEN} element={<GenericStatusScreen />} />
        <Route path={AppRoutes.AWAITING_TRANSACTION_CONFIRMATION} element={<AwaitingTransactionConfirmation />} />
        <Route path={AppRoutes.WALLET_TOKEN_SUCCESS} element={<WalletTokenSuccessScreen />} />
        <Route path={AppRoutes.THEME_DEMO} element={<ThemeSwitchDevPage />} />
        <Route path={AppRoutes.UNSUPPORTED_VERSION} element={<UnsupportedVersion />} />
        <Route path={AppRoutes.CIVIC_SIGNS_NOT_SUPPORTED} element={<CivicSignsNotSupported />} />
        <Route path={AppRoutes.INVALID_CONFIGURATION} element={<InvalidConfiguration />} />
      </Routes>

      <RevocationProvider>
        <Routes>
          <Route path={AppRoutes.FACE_SEARCH} element={<FaceSearchScreen />} />
          <Route path={AppRoutes.REVOCATION_QR_CODE} element={<RevocationQrCodeScreen />} />
          <Route path={AppRoutes.REVOCATION} element={<RevocationDisclaimerScreen />} />
          <Route path={AppRoutes.REVOCATION_PASSES_LIST} element={<RevocationPassesListScreen />} />
          <Route path={AppRoutes.REVOCATION_IN_REVIEW} element={<RevocationInReviewScreen />} />
          <Route path={AppRoutes.REVOCATION_SUCCESS} element={<RevocationConfirmationScreen />} />
        </Routes>
      </RevocationProvider>
    </>
  );
}
